import React, { useState } from "react";
import { css } from "glamor";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "@shared/env";
import CardPreview from "./CardPreview";
import "./ChangeCardForm.css";
import { createTextMask } from "redux-form-input-masks";
import { Form } from "semantic-ui-react";
import Button from "../library/Button";
import { CreditCardField, DocumentField, TextField } from "../library/forms";
import { spacing } from "../library/theme";
import { hasSpecialCharacters } from "../../utils/hasSpecialCharacters";

// --- Validators ---

const holderNameValidator = (value) => {
  if (value && hasSpecialCharacters(value)) return "specialCharacterOrNumber";
  return undefined;
};

const goodThruValidator = (value) => {
  if (!value) return "required";
  const parts = value.split("/");
  if (parts.length !== 2 || parts[0].length !== 2 || parts[1].length !== 2)
    return "invalid";
  const month = parseInt(parts[0], 10);
  if (month < 1 || month > 12) return "invalid";
  const year = parseInt(`20${parts[1]}`, 10);
  const now = new Date();
  if (
    year < now.getFullYear() ||
    (year === now.getFullYear() && month < now.getMonth() + 1)
  )
    return "expired";
  return undefined;
};

const holderNameValidationMessages = {
  specialCharacterOrNumber:
    "* Números e caracteres especiais não são permitidos",
  minWords: "* Colocar o nome como consta no cartão",
};

const goodThruValidationMessages = {
  invalid: "* A data de expiração não é válida",
  expired: "* Cartão expirado",
};

const cvvValidationMessages = {
  minSize: "* O CVV deve possuir 3 ou 4 caracteres",
  maxSize: "* O CVV deve possuir 3 ou 4 caracteres",
};

const goodThruMask = createTextMask({
  pattern: "99/99",
  guide: false,
  allowEmpty: false,
  stripMask: false,
});

// --- Styles ---

const cardPreviewWrapper = css({
  padding: "0 46px",
  marginBottom: spacing.m,
});

const cardFieldsWrapper = css({
  display: "grid",
  gridTemplateColumns: "repeat(8, 1fr)",
  gridGap: spacing.s,
  lineHeight: "normal",
});

const mobileFullWidth = css({
  "@media(max-width: 767px)": { gridColumn: "span 8" },
});

const fullWidthWrapper = css(mobileFullWidth, { gridColumn: "span 8" });
const halfWidthWrapper = css(mobileFullWidth, { gridColumn: "span 4" });

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 16,
  cursor: "pointer",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  color: "#475569",
  userSelect: "none",
};

const checkboxBoxStyle = (checked) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 16,
  height: 16,
  background: checked ? "#0369A1" : "#FFFFFF",
  border: `0.61px solid ${checked ? "#0369A1" : "#CBD5E1"}`,
  borderRadius: 4,
  transition: "background 0.15s, border-color 0.15s",
});

const recaptchaWrapper = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  marginTop: spacing.m,
});
const submitButtonStyle = {
  margin: 0,
  marginTop: 16,
};

const submitButtonClass = css({
  "&&": {
    borderRadius: "8px !important",
    backgroundColor: "#0F172A !important",
    fontFamily: "Montserrat, sans-serif !important",
    fontWeight: "600 !important",
    fontSize: "13px !important",
    lineHeight: "19.5px !important",
  },
  "&& .text": {
    fontFamily: "Montserrat, sans-serif !important",
    fontWeight: "600 !important",
    fontSize: "13px !important",
  },
});

// --- Component ---

export default function ChangeCardForm({
  handleSubmit,
  loading,
  disabled,
  cardNumber,
  holderName,
  goodThru,
  cvv,
  onDefaultChange,
  requiresCpf,
  showSetAsDefault,
  onRecaptchaChange,
}) {
  const [cvvFocused, setCvvFocused] = useState(false);
  const [previewCardNumber, setPreviewCardNumber] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);

  const handleFieldsInput = (e) => {
    if (e.target.name === "creditCard[number]") {
      setPreviewCardNumber(e.target.value);
    }
  };

  return (
    <Form className="add-card-form" onSubmit={handleSubmit}>
      <div className={cardPreviewWrapper}>
        <CardPreview
          cardNumber={previewCardNumber || cardNumber}
          holderName={holderName}
          goodThru={goodThru}
          cvv={cvv}
          flipped={cvvFocused}
        />
      </div>

      <div className={cardFieldsWrapper} onInput={handleFieldsInput}>
        {requiresCpf && (
          <div className={fullWidthWrapper}>
            <DocumentField
              name="cpf"
              label="CPF"
              placeholder="000.000.000-00"
              required
              fluid
            />
          </div>
        )}
        <div className={fullWidthWrapper}>
          <CreditCardField
            name="creditCard[number]"
            label="Número do cartão"
            placeholder="0000 0000 0000 0000"
            required
          />
        </div>
        <div className={fullWidthWrapper}>
          <TextField
            required
            name="creditCard[holderName]"
            label="Nome no cartão"
            placeholder="NOME COMO NO CARTÃO"
            validate={holderNameValidator}
            validationMessages={holderNameValidationMessages}
            minWords={2}
            shortMessages
            fluid
          />
        </div>
        <div className={halfWidthWrapper}>
          <TextField
            required
            name="goodThru"
            label="Validade"
            placeholder="MM/AA"
            fluid
            shortMessages
            validate={goodThruValidator}
            validationMessages={goodThruValidationMessages}
            {...goodThruMask}
          />
        </div>
        <div className={halfWidthWrapper}>
          <TextField
            required
            name="creditCard[cvv]"
            label="CVV"
            placeholder="123"
            maxSize={3}
            minSize={3}
            maxLength={3}
            validationMessages={cvvValidationMessages}
            fluid
            shortMessages
            onFocus={() => setCvvFocused(true)}
            onBlur={() => setCvvFocused(false)}
          />
        </div>
      </div>

      {showSetAsDefault && (
        <label style={checkboxLabelStyle}>
          <span style={checkboxBoxStyle(setAsDefault)}>
            <input
              type="checkbox"
              checked={setAsDefault}
              onChange={(e) => {
                setSetAsDefault(e.target.checked);
                if (onDefaultChange) onDefaultChange(e.target.checked);
              }}
              style={{
                position: "absolute",
                opacity: 0,
                width: "100%",
                height: "100%",
                margin: 0,
                cursor: "pointer",
              }}
            />
            {setAsDefault && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none" style={{ pointerEvents: "none" }}>
                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          Definir como pagamento principal
        </label>
      )}

      <div className={recaptchaWrapper}>
        <ReCAPTCHA onChange={onRecaptchaChange} hl="pt" sitekey={RECAPTCHA_SITE_KEY} />
      </div>

      <Button
        type="submit"
        loading={loading}
        disabled={disabled}
        color="black"
        fluid
        circular={false}
        className={submitButtonClass}
        style={submitButtonStyle}
      >
        Adicionar cartão
      </Button>
    </Form>
  );
}
